import { useId } from 'react';
import { Alert, Button, Form, Select, Tooltip } from 'antd';
import { ChevronDown, Copy, Trash2 } from 'lucide-react';
import type { CommandAskDto, CommandRoleDto, OrgUnitSummaryDto, RequirementLineInputDto, Slot } from '@pams/shared';
import type { RowControls } from '@/components/ReorderableList';
import { ForceGrid, type CoveredColumn } from '@/components/intake/ForceGrid';
import { CommandSection, ExtrasSection, PlacesSection, TimingSection } from '@/components/intake/Sections';
import { PlaceAutocomplete } from '@/components/intake/PlaceAutocomplete';
import { SectorField } from '@/components/intake/SectorField';
import { ZoneField } from '@/components/intake/ZoneField';
import { timePhrase } from '@/components/intake/timePhrase';
import type { ArrangementDraft } from '@/components/intake/draft';
import type { ArrangementDisclosure } from '@/lib/arrangementShape';
import { fromGrid, remainingByColumn, type ForceGrid as ForceGridModel } from '@/lib/forceGrid';
import { withChosenOption } from '@/lib/selectOptions';
import { cn } from '@/lib/utils';
import { DUTY_COPY, SHARE_COPY } from '@/pages/ArrangementBuilder/copy';

/**
 * ─── ONE DUTY POINT, AS A CARD ───────────────────────────────────────────────────
 *
 * ⚠️ THE BODY IS ALWAYS MOUNTED AND HIDDEN WITH A TAILWIND CLASS. Three things forbid
 * every other shape, and all three are recorded in CLAUDE.md §8:
 *
 *   · A Radix `Sheet` UNMOUNTS its content while closed (`ui/sheet.tsx`'s own header says
 *     so), which would delete this card's grid and command from every assertion that
 *     addresses them by test id without opening anything first.
 *   · A lazily-mounted AntD `Collapse` panel never REGISTERS its `Form.Item`s, so
 *     `Form.useWatch` and `onFinish` both answer zero for a closed card — the
 *     registered-fields-only trap that has bitten this codebase twice. (`ExtrasSection`
 *     needs `forceRender` for exactly this reason.)
 *   · The `hidden` ATTRIBUTE, `aria-hidden` and `inert` are all honoured by jsdom, so any
 *     of them takes the card out of the accessibility tree under test while the class does
 *     not — which is the property that lets one form be addressed whole.
 *
 * ⚠️ AND THERE IS NO WIDTH BRANCH. `vitest.setup.ts` stubs `matchMedia` to `matches: false`
 * for ever, so a width-branching render takes the narrow path in every test, silently.
 */

export type DutyPointCardProps = {
  index: number;
  /** The card's own minted identity. `deployments` key on it — never the index. */
  id: string;
  draft: ArrangementDraft;
  shape: ArrangementDisclosure;
  slots: readonly Slot[];
  grid: ForceGridModel;
  roles: readonly CommandRoleDto[];
  policies: readonly {
    id: string;
    name: string;
    description: string | null;
    isDefault: boolean;
    /**
     * ⚠️ [C2b §8e] WHAT THIS POLICY ASKS FOR — the post and vehicle columns it puts on the
     * force grid. OPTIONAL, so `StandingOrderBuilder` and this card's own tests are unaffected
     * ([D4.5 §1b]'s `resolvePolicy` precedent, one field over).
     */
    columns?: readonly string[];
  }[];
  /**
   * ⚠️ [D4.5 §1b] WHAT A STORED `policyId` RENDERS AS, WHICH IS NOT THE SAME SET AS
   * `policies`. That list is what this card may CHOOSE — unarchived, with a published
   * version. An order raised before a policy was archived still points at it, and without
   * this the `Select` had no option for that value and rc-select painted the ObjectId.
   * Optional, so `StandingOrderBuilder` and the card's own tests are unaffected.
   */
  resolvePolicy?: (id: string) => { name: string } | undefined;
  policyName: string;
  /**
   * ⚠️ [PRE-P6 §3a] WHICH VERSION THIS CARD WILL BIND, STATED WHERE THE CHOICE IS MADE.
   *
   * A policy's columns live on a VERSION, and which one an order gets is not obvious from
   * anywhere on this screen: publishing v2 leaves the picker reading the same name, and
   * saving a DRAFT version leaves the policy on v1 while `PolicyBuilder` has just said
   * "Draft v2 saved". `null` while the version is still loading, and the row is then
   * omitted rather than showing a placeholder that flickers into a number.
   */
  policyVersionLabel: string | null;
  officers: ReadonlyMap<string, { name: string }>;
  orgUnits: ReadonlyMap<string, OrgUnitSummaryDto>;
  /**
   * ⚠️ [C6a §2] THIS CARD'S SAVED ASKED COMMAND POSITIONS, BY ASK ID — what `CommandSection` names a
   * stored addressee from and, once issued, reads who each division named. THIS card's only: an ask
   * id is unique on its duty point, not across the order. Optional, so `StandingOrderBuilder` and a
   * card rendered alone are unaffected.
   */
  commandAsks?: ReadonlyMap<string, CommandAskDto>;
  /** ⚠️ [C6a review] REQUIRED — whether an unnamed asked position is still "awaited" (`CommandSection`). */
  awaitedLive: boolean;
  forks: boolean;
  /** ⚠️ [C1a §2] The columns this card asks for that its bound version does not carry. */
  forkColumns: readonly string[];
  /**
   * ⚠️ [C1a §2] SET ONLY WHEN THE POLICY MOVED UNDER AN UNPINNED CARD AND THE COLUMN SET
   * ACTUALLY CHANGED. `undefined` for a pinned card (an existing order is frozen — [P2],
   * defended by [FIX-1]), and `undefined` for a version bump that only renamed or reordered,
   * because there is nothing for the operator to decide.
   */
  versionMoved?: { label: string; added: readonly string[]; dropped: readonly string[] };
  open: boolean;
  readOnly: boolean;
  /**
   * ⚠️ [C3c §9b] FILLING HAS BEGUN, SO THIS CARD'S STRUCTURE IS FIXED — and it is a SEPARATE
   * flag from `readOnly`, which means "this operator may not write".
   *
   * It locks exactly what `assertPublishedDutyPointsUnchanged` already refuses on the server —
   * the PLACES, the SCHEDULE, the POLICY VERSION and the card's own existence — and NOTHING
   * else. Force counts, command entries and slot overrides stay editable because the server
   * ALLOWS them and FR-5.2.13 requires it; locking the whole card would refuse an act the API
   * accepts. This is the OFFER gate, and that function is the control.
   *
   * ⚠️ DEFAULTED, so `StandingOrderBuilder` and every other consumer are byte-identical.
   */
  structureLocked?: boolean;
  /**
   * ⚠️ [C4a §4b] THE ZONES TO SUGGEST — the OTHER cards' on this order, from `zoneOptionsFor`. The card
   * cannot compute them: it holds its own draft and nobody else's. Defaulted to none, so a card
   * rendered alone offers nothing rather than failing to compile.
   */
  zoneOptions?: readonly string[];
  /** [C5c §4] THE SECTORS TO SUGGEST — `zoneOptions`' rule, from `sectorOptionsFor`, defaulted to none. */
  sectorOptions?: readonly string[];
  /**
   * ⚠️ [amended D4.1] `ReorderableList`'s controls, placed in this card's HEADER rather than
   * in a flex row beside the card. ⚠️ [amended D4.2] Two pieces now: the grip goes left with
   * the chevron, the overflow goes right past `Duplicate`. Optional so the card renders alone.
   */
  controls?: RowControls | null;
  onToggle: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  /** ⚠️ [C1a §2] Re-seeds this card from its bound version, KEEPING every count that survives. */
  onUseCurrentColumns: () => void;
  onAddShift: () => void;
  onAddPlace: () => void;
  onPolicyChange: (policyId: string) => void;
  /**
   * ⚠️ [C2b §8c/§8d] THE PAGE'S `onValuesChange`, FOR WRITES THAT GO THROUGH `setFieldValue`.
   *
   * rc-field-form does not fire `onValuesChange` from `setFieldValue`, so a command-role edit
   * left the form CLEAN and autosave answered `'nothing-to-save'` on every trigger — losing an
   * officer selection, a per-shift mode and a custom role alike. `CommandSection` requires it;
   * this card is its only route to the page. See `Sections.tsx`'s `CommandSection` header.
   */
  onCommandEntriesChange: () => void;
  onRequirementLines: (lines: RequirementLineInputDto[]) => void;
  /**
   * FR-5.2.6c — `Add column`. ⚠️ OPTIONAL, AND ITS ABSENCE IS WHAT HIDES THE BUTTON.
   * `ForceGrid` gates the control on this prop, so a caller with no notion of a one-off
   * column (the standing-order builder) omits it and renders no dead affordance.
   */
  onAddAdHoc?: ((label: string) => void) | undefined;
  onRemoveAdHoc?: ((lineIndex: number) => void) | undefined;
  /** ⚠️ [C1c §4a] One-off columns only — see `ForceGrid`'s prop for the whole argument. */
  onRestrictAdHoc?: ((lineIndex: number, postTypeIds: readonly string[]) => void) | undefined;
  postTypeOptions?: readonly { id: string; name: string }[];
  /**
   * ⚠️ [C5c §2] SHARED COLUMNS — forwarded to `ForceGrid`, whose props say what each one is.
   * All optional, so `StandingOrderBuilder` and this card's own tests are unaffected.
   */
  coveredColumns?: readonly CoveredColumn[];
  sharedLineIndexes?: ReadonlySet<number>;
  onToggleShare?: ((lineIndex: number) => void) | undefined;
  refuseAdHoc?: ((label: string) => string | undefined) | undefined;
  /**
   * ⚠️ [C5c §2f] WHILE ANY COLUMN IS SHARED, EVERY CARD BINDS ONE POLICY VERSION, so the picker is
   * locked and says why. A SEPARATE flag from `structureLocked`: that one is a lifecycle fact about
   * the record, this one is a consequence of a choice the operator can undo on this screen.
   */
  policyLockedBySharing?: boolean;
};

/**
 * ⚠️ CLAMPED PER COLUMN. An over-allocated column would otherwise make `sourced` exceed
 * `asked` and the chip would read "12 of 10 sourced", which is not a sentence.
 */
const sourcedOf = (grid: ForceGridModel): { sourced: number; asked: number } => {
  const asked = grid.totals.reduce((sum, total) => sum + total, 0);
  const short = remainingByColumn(grid).reduce((sum, value) => sum + Math.max(value, 0), 0);
  return { sourced: asked - short, asked };
};

export const DutyPointCard = ({
  index,
  id,
  draft,
  shape,
  slots,
  grid,
  roles,
  policies,
  resolvePolicy,
  policyName,
  policyVersionLabel,
  officers,
  orgUnits,
  commandAsks,
  awaitedLive,
  forks,
  forkColumns,
  versionMoved,
  open,
  controls,
  readOnly,
  structureLocked = false,
  zoneOptions = [],
  sectorOptions = [],
  onToggle,
  onDuplicate,
  onRemove,
  onAddShift,
  onAddPlace,
  onPolicyChange,
  onCommandEntriesChange,
  onRequirementLines,
  onAddAdHoc,
  onRemoveAdHoc,
  onRestrictAdHoc,
  postTypeOptions,
  onUseCurrentColumns,
  coveredColumns,
  sharedLineIndexes,
  onToggleShare,
  refuseAdHoc,
  policyLockedBySharing = false,
}: DutyPointCardProps) => {
  const bodyId = useId();
  const { sourced, asked } = sourcedOf(grid);
  const where = draft.locationNames[0] ?? `Duty point ${String(index + 1)}`;
  /*
   * ⚠️ [amended D4.2] THE INDEX IS PART OF THE TITLE, NOT A BADGE BESIDE IT. `ReorderableList`
   * rendered a bare position number in its own 20px box; a card that already says
   * "Duty point 1" when it has no place did not need it twice, and a card that HAS a place
   * reads better as the place. `where` stays as it is for every accessible name that used it.
   */
  const title = where;
  /*
   * ⚠️ [C6a §2] NAMED OFFICERS ONLY. A role asked of a division carries no `officerIds` — the asks are
   * positions a division will fill, not people — so they add nothing here, and must not: "N in
   * command" beside "M of K sourced" is the one place on this card a command number sits near a
   * force one (FR-5.2.6b). `CommandSection.test.tsx` renders this header with asks and pins it.
   */
  const named = draft.commandEntries.reduce((sum, entry) => sum + entry.officerIds.length, 0);

  /*
   * ⚠️ [C2b §1a] THE POLICY CONTROL, BUILT HERE AND PLACED BY `TimingSection`.
   *
   * Everything it reads — the offerable list, the archived-policy resolver, the bound version
   * label, the fork notice and the version-moved offer — is this card's, which is why the JSX
   * stays here and only its POSITION moved. Its own `Form.Item` paths are unchanged, so every
   * assertion that addresses `duty-point-policy-*` keeps working wherever the block lands.
   */
  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C2b §1a] THE POLICY **CONTROL**, PLACED BY `TimingSection` IN THE `Arrangement type` ROW.
   *  [amended C5c §4] On a line of its own beneath type, zone and sector, at every width — see
   *  that row's comment in `Sections.tsx` for the measurement.
   *
   *  The policy decides the force grid's COLUMNS, so it belongs above that grid rather than
   *  below the schedule: reading order is place → type, zone, sector, policy → dates → force.
   *
   *  The select takes its natural width rather than the row's — a policy name is a few words
   *  and a full-width select over it reads as a much bigger decision than it is.
   *
   *  ⚠️ THE NOTICES ARE A SEPARATE VARIABLE AND STAY IN THIS CARD, AND THAT IS MEASURED.
   *  Moving them into the slot too put an antd `Alert` inside a `flex-wrap` row that re-lays
   *  out on every keystroke of the force grid, and the [C1a §2] re-seed test went from 12.8s
   *  to 41.7s — through its own 30s budget. Split, the same test runs in 7.5s, FASTER than
   *  before the move. The split is also what this block's original comment already asked for:
   *  "the fork notice keeps its own line, because it is a sentence and not a field."
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const policyBlock = (
    <div className="flex flex-wrap items-center gap-3">
      <span className="shrink-0 text-sm">{DUTY_COPY.policy}</span>
      <Form.Item name={['dutyPoints', index, 'policyId']} className="mb-0 min-w-56">
        <Select
          showSearch
          optionFilterProp="label"
          /* ⚠️ [C3c §9b] And the POLICY VERSION — the fourth. [C5c §2f] And while a column is shared. */
          disabled={readOnly || structureLocked || policyLockedBySharing}
          data-testid={`duty-point-policy-${String(index)}`}
          aria-label={`Policy for ${where}`}
          onChange={onPolicyChange}
          /* ⚠️ [D4.5 §1b] An archived policy this card already points at keeps its
                   NAME rather than rendering its id, and is still not offered to anyone
                   choosing afresh — `lib/selectOptions.ts`. */
          /*
           * ⚠️ [C2b §8e] THE PREVIEW IS THE OPTION'S NATIVE `title`, NEVER A `Tooltip`.
           *
           * `policyNotices` below already records the argument in full and it applies here
           * unchanged: `census.spec.ts` sweeps six overlay component NAMES and would demand a
           * fixture entry for a seventh, jsdom resolves no cascade so an overlay's content is
           * unassertable in the unit suite, and [D4.5] MEASURED an AntD `Tooltip` per list row
           * taking one test file from 64s to 119s — this is a row per policy in a dropdown.
           *
           * ⚠️ AND THE COLUMNS COME FIRST. The description is a sentence somebody typed once;
           * the columns are what choosing this option does to the grid below.
           */
          options={withChosenOption(
            policies.map((policy) => ({
              value: policy.id,
              label: policy.name,
              title:
                policy.columns && policy.columns.length > 0
                  ? [policy.columns.join(' · '), policy.description].filter(Boolean).join(' — ')
                  : (policy.description ?? undefined),
            })),
            draft.policyId || null,
            resolvePolicy,
          )}
        />
      </Form.Item>
      {policyVersionLabel !== null && (
        <span
          className="shrink-0 text-xs text-muted-foreground"
          data-testid={`duty-point-policy-version-${String(index)}`}
        >
          {policyVersionLabel}
        </span>
      )}
    </div>
  );

  /*
   * ⚠️ [C2b §1a] THE SENTENCES ABOUT THE POLICY, BELOW THE SCHEDULE — see `policyBlock`.
   * They are prose rather than fields, and keeping them out of the type/policy row is what
   * keeps that row a row.
   */
  const policyNotices = (
    <>
      {/*
       * ⚠️ [C1a §2] THE POLICY MOVED UNDER THIS CARD, AND IT IS AN OFFER RATHER THAN A
       * REWRITE. Silently re-seeding would delete numbers the operator has typed, which
       * is the one thing the seeding effect's own guard has always promised not to do;
       * doing nothing leaves the `v2` label sitting over v1's columns, which is the
       * reported defect. So the card says what changed, what updating would cost, and
       * what happens if it is ignored — `PolicyBuilder`'s "an OFFER, not an automatic
       * removal… the button says exactly how many" applied one screen over.
       *
       * ⚠️ AN INLINE `Alert`, NEVER A Tooltip/Popover/Dropdown. `census.spec.ts` sweeps
       * those six component names and would demand a fixture entry, and jsdom resolves
       * no cascade — so an overlay would make this claim unassertable in the unit suite
       * for no gain.
       */}
      {versionMoved && (
        <Alert
          className="mt-2"
          type="warning"
          showIcon
          data-testid={`duty-point-version-moved-${String(index)}`}
          message={DUTY_COPY.versionMoved.title(versionMoved.label)}
          description={
            <div className="flex flex-col items-start gap-2">
              <span>{DUTY_COPY.versionMoved.body(versionMoved.label, versionMoved.added, versionMoved.dropped)}</span>
              {!readOnly && (
                <Button size="small" onClick={onUseCurrentColumns}>
                  {DUTY_COPY.versionMoved.action}
                </Button>
              )}
            </div>
          }
        />
      )}
      {/* ⚠️ [C5c §2f] The reason is on screen, not only in a disabled control — [C3c §9b]'s rule. */}
      {policyLockedBySharing && !readOnly && !structureLocked && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid={`duty-point-policy-shared-${String(index)}`}>
          {SHARE_COPY.policyLocked}
        </p>
      )}
      {/* ⚠️ ONE FACT, ONE SENTENCE. When the policy moved under the card, THAT is what
              happened — the fork notice would be describing the operator's own doing. */}
      {forks && !versionMoved && (
        <p className="mt-2 text-xs text-warning" data-testid={`duty-point-fork-${String(index)}`}>
          {DUTY_COPY.forkNotice(forkColumns)}
        </p>
      )}
    </>
  );

  return (
    /*
     * ⚠️ [amended D4.2] THE CARD GETS ITS BOUNDARY BACK — ONE BORDER, AND EXACTLY ONE.
     *
     * D4.1 removed four levels of nesting and took the container with it, so a duty point
     * sat on the page background with nothing saying where one ended and the next began.
     * This is the boundary put back at the level it belongs to: `bg-card` on the page's
     * `bg-background`, one hairline, and the stack's own dividers are gone with it — two
     * boundaries between the same two objects is the box-in-box D4.1 removed.
     *
     * ⚠️ `rounded-xl` IS 10px, NOT THE 12 THE BRIEF ASKED FOR, and that is deliberate.
     * `tokens.ts` is the one source of shape as well as colour, its scale stops at
     * `radiusLargePx: 10`, and the design language's own anti-pattern list caps card radius
     * at 12. An arbitrary `rounded-[12px]` would be a literal outside the token system for
     * two pixels nobody can see.
     *
     * ⚠️ `@container/dutypoint` IS LOAD-BEARING. Every two-column row inside this card
     * measures THE CARD. An unnamed query would resolve to `<main>`, which is 1062 at the
     * 1366 floor even while this card has 638 beside an open sidebar — so the rows would
     * pair in a column that cannot hold them. NAMED rather than unnamed for the reason
     * `AppShell` names the shell row: an unnamed container here would silently re-point any
     * `@min-` a descendant writes later. Same precedent as the OrgTree drawer.
     */
    <section
      id={`duty-point-${id}`}
      data-testid={`duty-point-${String(index)}`}
      className="@container/dutypoint rounded-xl border border-card-outline bg-card"
    >
      {/*
        ⚠️ [amended D4.2] LEFT: GRIP AND CHEVRON. RIGHT: DUPLICATE, DELETE, OVERFLOW.
        The header carried eight elements — grip, index, up, down, overflow, chevron, name,
        summary — before an operator reached the name. The move pair went into the overflow
        (`ReorderableList`'s menu carries it now, with the same labels and the same disabled
        rule), and the index is part of the title rather than a badge of its own.

        The hairline is INSIDE the card and only while open, because a rule under a collapsed
        header is a line under nothing.
      */}
      <div className={cn('flex flex-wrap items-center gap-2 px-3 py-2', open && 'border-b border-card-outline')}>
        {controls?.handle}
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          data-testid={`duty-point-toggle-${String(index)}`}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={onToggle}
        >
          <ChevronDown className={cn('size-4 shrink-0 text-muted-foreground', !open && '-rotate-90')} aria-hidden />
          <span className="min-w-0 truncate text-sm font-medium">{title}</span>
          {/*
            ⚠️ ONE SEPARATOR, AND IT IS RENDERED BETWEEN THE PARTS RATHER THAN INSIDE THEM.
            The middot used to live inside `timePhrase` while the other three spans were
            divided by nothing but a flex gap, so the line read
            `Whole day · 2026-08-16Standard PA0 of 0 sourced`. Joining a filtered array is
            what makes "one separator throughout" true by construction.
          */}
          <span
            className="min-w-0 truncate text-xs text-muted-foreground"
            data-testid={`duty-point-sourced-${String(index)}`}
          >
            {[
              timePhrase(draft),
              policyName,
              DUTY_COPY.sourced(sourced, asked),
              named > 0 ? `${String(named)} in command` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </button>

        <Tooltip title={DUTY_COPY.duplicateHint}>
          <Button
            size="small"
            htmlType="button"
            icon={<Copy className="size-3.5" />}
            disabled={readOnly || structureLocked}
            data-testid={`duty-point-duplicate-${String(index)}`}
            onClick={onDuplicate}
          >
            {DUTY_COPY.duplicate}
          </Button>
        </Tooltip>
        <Button
          size="small"
          htmlType="button"
          icon={<Trash2 className="size-3.5" />}
          disabled={readOnly || structureLocked}
          aria-label={`${DUTY_COPY.remove} ${title}`}
          data-testid={`duty-point-remove-${String(index)}`}
          onClick={onRemove}
        />
        {controls?.menu}
      </div>

      {/*
        ⚠️ ALWAYS MOUNTED. See this file's header for the three reasons.

        ⚠️ [amended D4.2] FIVE BANDS, NOT ELEVEN. `Where` and `When` were section headings
        sitting directly above fields labelled `Duty place` and `Times`, saying the same
        thing twice; `Policy` was a heading over a single select. All three are gone —
        `FORCE REQUIRED` is the only heading left, because the grid under it genuinely needs
        a title. Grouping is spacing and hairlines, and no group inside here has a box.
      */}
      <div id={bodyId} className={cn('flex flex-col gap-5 p-3', !open && 'hidden')}>
        {/*
          ── BAND 1: duty place | reporting place — 60/40 ────────────────────────────────
          ⚠️ [amended C2b §1b/§1e] REPORTING TIME LEFT THIS ROW. It sits on the DATE row now,
          on the two whole-day combinations only. ⚠️ [amended C5b §3e] On the other four a card has
          none: each duty reports at its own shift start — see `TimingSection` and `reportingTimeOf`.

          So "where and when to report" is split across two rows, deliberately: the reporting
          PLACE is a place and belongs beside the duty place; the TIME qualifies the dates.

          ⚠️ [amended C1d §3a] THE 60/20/20 SHAPE WAS FOR THREE CHILDREN AND THERE ARE TWO.
          Place names run long and the duty place is the primary field, so it keeps the larger
          share rather than the row splitting evenly.

          ⚠️ AND NO NEW BREAKPOINT. 880px already stacks this card to one column and is shared
          with `TimingSection`'s band; a new arbitrary container-query value would have to be a
          bare pixel length even inside this comment, because Tailwind extracts candidates from
          source TEXT and `containerQueryCandidates.test.ts` fails the build otherwise.
        */}
        {/*
          ─────────────────────────────────────────────────────────────────────────────────
           ⚠️ [C2b §1a] THE HIDDEN REGISTRATION FIELDS STAY IN THIS CARD, AT A FIXED POSITION,
           AND THAT IS A MEASURED DECISION RATHER THAN A TIDY ONE.

           They travelled with the policy control at first, into `TimingSection`'s slot — which
           puts them after that section's CONDITIONAL blocks (the weekday chips, the ends-kind
           pair, the shift rows). React reconciles children by type and POSITION, so a toggle
           above them remounts them, and a remounted `Form.Item` DEREGISTERS and re-registers
           its path in rc-field-form. MEASURED: the [C1a §2] re-seed test went from 12.8s to
           41.7s and blew its own 30s budget.

           ⚠️ THE COST WAS SPEED AND THE RISK WAS THE RECORD. A field that deregisters is a
           field `getFieldsValue()` cannot see — the exact trap this repository has recorded
           three times — and `columnsFromVersionId` is what the whole columns-versus-binding
           distinction is carried on. Only the VISIBLE control moved; what the form STORE
           depends on did not.

           ⚠️ REGISTERED, so `onFinish` carries them. An unregistered field never reaches the
           payload. `columnsFromVersionId` is form-only — `toDutyPointInput` enumerates its
           fields, so it never reaches the wire.
          ─────────────────────────────────────────────────────────────────────────────────
        */}
        <Form.Item name={['dutyPoints', index, 'id']} hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name={['dutyPoints', index, 'policyVersionId']} hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name={['dutyPoints', index, 'columnsFromVersionId']} hidden>
          <input type="hidden" />
        </Form.Item>

        {/*
          ─────────────────────────────────────────────────────────────────────────────
           ⚠️ [C5 §10a] REPORTING PLACE LEFT AT 60%, DUTY PLACE AND ITS ADD-STOP CONTROL RIGHT
           AT 40%. A SOURCE-ORDER SWAP, AND NOTHING ELSE.

           `grid-cols-[3fr_2fr]` IS 60/40 and does not move; the container query
           (`@min-[880px]/dutypoint`) and its threshold do not move either, so §10c is satisfied
           by there being no new breakpoint at all. What changed is which child is first.

           ⚠️ SOURCE ORDER, NEVER `order-*`. `lib/twoColumn.ts` states that rule and
           `twoColumn.test.ts` guards it for the shared strings: a visual order that disagrees
           with the DOM order is one a keyboard and a screen reader walk in the wrong sequence.

           ⚠️ THE ADD-STOP CONTROL TRAVELS ON ITS OWN. It lives inside `PlacesSection`'s header
           (`Sections.tsx`, `data-testid="add-place"`), not here, so moving the section moves it.
          ─────────────────────────────────────────────────────────────────────────────
        */}
        <div className="grid grid-cols-1 gap-4 @min-[880px]/dutypoint:grid-cols-[3fr_2fr]">
          <Form.Item name={['dutyPoints', index, 'reportingPlace']} label={DUTY_COPY.reportingPlace} className="mb-0">
            <PlaceAutocomplete
              disabled={readOnly || structureLocked}
              data-testid={`reporting-place-${String(index)}`}
            />
          </Form.Item>
          <PlacesSection
            draft={draft}
            /* ⚠️ [C3c §9b] A PLACE IS ONE OF THE FOUR THINGS THE SERVER FREEZES ONCE PUBLISHED. */
            readOnly={readOnly || structureLocked}
            namePath={['dutyPoints', index]}
            label={DUTY_COPY.dutyPlace}
            onAddPlace={onAddPlace}
          />
        </div>

        {/*
          ── BANDS 2–5: type and policy, the dates, and the shift rows. ─────────────────
          ⚠️ [C2b §1a] THE POLICY CONTROL IS HANDED IN rather than rendered below the schedule.
          It decides the force grid's COLUMNS, so it belongs above that grid; reading order is
          place → type and policy → dates → force. It stays JSX owned by THIS card because it
          reads eight of the card's props, and `TimingSection` has none of them.
        */}
        <TimingSection
          draft={draft}
          /* ⚠️ [C3c §9b] The SCHEDULE, likewise — `assertPublishedDutyPointsUnchanged` compares
             the normalised recurrence and shifts and refuses any change to either. */
          readOnly={readOnly || structureLocked}
          slots={slots}
          showsSlotStrip={shape.showsSlotStrip}
          namePath={['dutyPoints', index]}
          onAddShift={onAddShift}
          policySlot={policyBlock}
          /* ⚠️ [C4a §4c] BESIDE THE TYPE, BEFORE THE POLICY. `readOnly` alone — the server freezes no zone. */
          zoneSlot={
            <ZoneField
              index={index}
              options={zoneOptions}
              disabled={readOnly}
              label={DUTY_COPY.zone}
              placeholder={DUTY_COPY.zonePlaceholder}
            />
          }
          /* [C5c §4] RIGHT AFTER THE ZONE, BEFORE THE POLICY — and, like it, `readOnly` alone. */
          sectorSlot={
            <SectorField
              index={index}
              options={sectorOptions}
              disabled={readOnly}
              label={DUTY_COPY.sector}
              placeholder={DUTY_COPY.sectorPlaceholder}
            />
          }
        />

        {policyNotices}

        {/* ── BAND 6: the force grid. The one block that keeps a heading. ──────────────── */}
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {DUTY_COPY.force}
          </h3>
          <Form.Item name={['dutyPoints', index, 'requirementLines']} hidden>
            <input type="hidden" />
          </Form.Item>
          <ForceGrid
            flush
            grid={grid}
            orgUnits={orgUnits}
            readOnly={readOnly}
            context={where}
            data-testid={`force-grid-${String(index)}`}
            onChange={(next) => {
              onRequirementLines(fromGrid(next, draft.requirementLines));
            }}
            onAddAdHoc={onAddAdHoc}
            onRemoveAdHoc={onRemoveAdHoc}
            onRestrictAdHoc={onRestrictAdHoc}
            postTypeOptions={postTypeOptions}
            coveredColumns={coveredColumns}
            sharedLineIndexes={sharedLineIndexes}
            onToggleShare={onToggleShare}
            refuseAdHoc={refuseAdHoc}
          />
        </div>

        {/*
          Command, when the policy defines any. ⚠️ [amended D4.2] A policy with no command
          roles rendered a heading over one grey sentence — a whole band saying there is
          nothing here. The roles are the block; with none there is no block.
        */}
        {(roles[0] !== undefined || draft.commandEntries.some((entry) => entry.adHocLabel) || !readOnly) && (
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {DUTY_COPY.command}
            </h3>
            <CommandSection
              draft={draft}
              readOnly={readOnly}
              roles={roles}
              /* ⚠️ [C6a §2] `policyVersionLabel` is `null` exactly when the bound version is not in hand —
                 in flight, no policy, a failed read (index.tsx builds both from one `version`) — and
                 `roles` is `[]` then. So it is the card's own "the roles are known", without which the
                 dropped-role row may not fire. */
              rolesKnown={policyVersionLabel !== null}
              slots={slots}
              perSlotMapperRoleKeys={shape.perSlotMapperRoleKeys}
              officers={officers}
              namePath={['dutyPoints', index]}
              context={where}
              onChanged={onCommandEntriesChange}
              /* ⚠️ [C6a §2] Asks are settled at publish — the card's own structure lock, [C3c.1 §9b]'s status test. */
              asksLocked={structureLocked}
              commandAsks={commandAsks}
              awaitedLive={awaitedLive}
              orgUnits={orgUnits}
            />
          </div>
        )}

        {/*
          ⚠️ [D4.3] BELOW FORCE AND COMMAND — the standing orders for THIS place.
          Reopening OD-20 (see `sections.tsx`'s `extras` note). The SAME component the PA-level
          panel uses, prefixed: if a section ever has to be copied to make a form work, the
          shared intake library has stopped doing its job. It stays collapsed by default and
          keeps `forceRender`, without which its `Form.Item`s never register and every
          derivation reading them silently answers zero ([FIX-3]).
        */}
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {DUTY_COPY.instructions}
          </h3>
          <ExtrasSection readOnly={readOnly} namePath={['dutyPoints', index]} context={where} />
        </div>
      </div>
    </section>
  );
};
